import React, { useEffect, useState, useRef } from 'react';
import { Loader2, ChevronDown, X } from 'lucide-react';
import { VINTED_COLORS, VintedColor } from '../../utils/vintedColors';
import { BrandAutocomplete } from './BrandAutocomplete';
import { SelectedBrand, SelectedSize, SelectedMaterial, SelectedPattern, SelectedLanguage, SelectedPlatform, SelectedRating } from '../../types/alert.types';
import { useCategoryFiltersWithOptions } from '../../hooks/useCategoryFiltersWithOptions';

interface Step4CategoryFiltersProps {
  categoryId: string;
  categoryPath: string;
  selectedConditions: string[];
  selectedSizes: SelectedSize[];
  selectedColors: string[];
  selectedBrands: SelectedBrand[];
  selectedStatuses: number[];
  selectedLanguages?: SelectedLanguage[];
  selectedMaterials?: SelectedMaterial[];
  selectedPatterns?: SelectedPattern[];
  selectedPlatforms?: SelectedPlatform[];
  selectedRatings?: SelectedRating[];
  onConditionsChange: (conditions: string[]) => void;
  onSizesChange: (sizes: SelectedSize[]) => void;
  onColorsChange: (colors: string[]) => void;
  onBrandsChange: (brands: SelectedBrand[]) => void;
  onStatusesChange: (statuses: number[]) => void;
  onLanguagesChange?: (languages: SelectedLanguage[]) => void;
  onMaterialsChange?: (materials: SelectedMaterial[]) => void;
  onPatternsChange?: (patterns: SelectedPattern[]) => void;
  onPlatformsChange?: (platforms: SelectedPlatform[]) => void;
  onRatingsChange?: (ratings: SelectedRating[]) => void;
}

export const Step4CategoryFilters: React.FC<Step4CategoryFiltersProps> = ({
  categoryId,
  categoryPath,
  selectedConditions,
  selectedSizes,
  selectedColors,
  selectedBrands,
  selectedStatuses,
  selectedLanguages = [],
  selectedMaterials = [],
  selectedPatterns = [],
  selectedPlatforms = [],
  selectedRatings = [],
  onConditionsChange,
  onSizesChange,
  onColorsChange,
  onBrandsChange,
  onStatusesChange,
  onLanguagesChange,
  onMaterialsChange,
  onPatternsChange,
  onPlatformsChange,
  onRatingsChange
}) => {
  // Utiliser le nouveau hook qui charge filtres + options
  // categoryPath requis pour le filtre taille (vinted_sizes.json par catégorie)
  const { filters, loading, error } = useCategoryFiltersWithOptions(
    categoryId ? parseInt(categoryId) : null,
    categoryPath || null
  );

  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const sizeDropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown taille au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(e.target as Node)) {
        setSizeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleColor = (color: string) => {
    if (selectedColors.includes(color)) {
      onColorsChange(selectedColors.filter(c => c !== color));
    } else {
      onColorsChange([...selectedColors, color]);
    }
  };

  const toggleStatus = (statusId: number) => {
    if (selectedStatuses.includes(statusId)) {
      onStatusesChange(selectedStatuses.filter(id => id !== statusId));
    } else {
      onStatusesChange([...selectedStatuses, statusId]);
    }
  };

  const toggleLanguage = (languageId: number, languageTitle: string) => {
    if (!onLanguagesChange) return;
    const languageIdStr = String(languageId);
    const isSelected = selectedLanguages.some(l => l.id === languageIdStr);
    if (isSelected) {
      onLanguagesChange(selectedLanguages.filter(l => l.id !== languageIdStr));
    } else {
      onLanguagesChange([...selectedLanguages, { id: languageIdStr, title: languageTitle }]);
    }
  };

  const toggleMaterial = (materialId: number, materialTitle: string) => {
    if (!onMaterialsChange) return;
    const isSelected = selectedMaterials.some(m => m.id === materialId);
    if (isSelected) {
      onMaterialsChange(selectedMaterials.filter(m => m.id !== materialId));
    } else {
      onMaterialsChange([...selectedMaterials, { id: materialId, title: materialTitle }]);
    }
  };

  const togglePattern = (patternId: number, patternTitle: string) => {
    if (!onPatternsChange) return;
    const isSelected = selectedPatterns.some(p => p.id === patternId);
    if (isSelected) {
      onPatternsChange(selectedPatterns.filter(p => p.id !== patternId));
    } else {
      onPatternsChange([...selectedPatterns, { id: patternId, title: patternTitle }]);
    }
  };

  const togglePlatform = (platformId: number, platformTitle: string) => {
    if (!onPlatformsChange) return;
    const isSelected = selectedPlatforms.some(p => p.id === platformId);
    if (isSelected) {
      onPlatformsChange(selectedPlatforms.filter(p => p.id !== platformId));
    } else {
      onPlatformsChange([...selectedPlatforms, { id: platformId, title: platformTitle }]);
    }
  };

  const toggleRating = (ratingId: number, ratingTitle: string) => {
    if (!onRatingsChange) return;
    const isSelected = selectedRatings.some(r => r.id === ratingId);
    if (isSelected) {
      onRatingsChange(selectedRatings.filter(r => r.id !== ratingId));
    } else {
      onRatingsChange([...selectedRatings, { id: ratingId, title: ratingTitle }]);
    }
  };

  const toggleSize = (sizeId: number, sizeTitle: string) => {
    const isSelected = selectedSizes.some(s => s.id === sizeId);
    if (isSelected) {
      onSizesChange(selectedSizes.filter(s => s.id !== sizeId));
    } else {
      onSizesChange([...selectedSizes, { id: sizeId, title: sizeTitle }]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Filtres de la catégorie</h3>
        {categoryPath ? (
          <p className="text-sm text-gray-600">📂 {categoryPath}</p>
        ) : (
          <p className="text-sm text-gray-600">Aucune catégorie sélectionnée - Filtres généraux disponibles</p>
        )}
      </div>

      {/* Chargement */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
          <span className="ml-2 text-gray-600">
            En chargement
          </span>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 text-sm">❌ {error}</p>
        </div>
      )}

      {/* Marques - Affichée uniquement si détectée */}
      {filters?.brand?.present && (
        <BrandAutocomplete
          selectedBrands={selectedBrands}
          onBrandsChange={onBrandsChange}
        />
      )}

      {/* États - Utilise les options du JSON */}
      {filters?.status?.present && filters.status.options && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            💎 {filters.status.french_name}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {filters.status.options.map(status => (
              <label
                key={status.id}
                className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes(status.id)}
                  onChange={() => toggleStatus(status.id)}
                  className="w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500"
                />
                <span className="text-sm">{status.title}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Laissez vide pour tous les états
          </p>
        </div>
      )}

      {/* Tailles - Utilise les options de l'API Vinted */}
      {filters?.size?.present && filters.size.options && filters.size.options.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            📏 {filters.size.french_name}
          </label>
          <div className="space-y-3">
            {/* Dropdown personnalisé - s'ouvre vers le bas */}
            <div className="relative" ref={sizeDropdownRef}>
              <button
                type="button"
                onClick={() => setSizeDropdownOpen(!sizeDropdownOpen)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white cursor-pointer text-left flex items-center justify-between"
              >
                <span className="text-gray-500">Sélectionner une taille...</span>
                <ChevronDown
                  className={`text-gray-400 transition-transform ${sizeDropdownOpen ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              {sizeDropdownOpen && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto top-full">
                  {filters.size.options.map(size => {
                    const isSelected = selectedSizes.some(s => s.id === size.id);
                    return (
                      <button
                        key={size.id}
                        type="button"
                        onClick={() => {
                          if (!isSelected) {
                            toggleSize(size.id, size.title);
                          }
                          setSizeDropdownOpen(false);
                        }}
                        disabled={isSelected}
                        className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                          isSelected
                            ? 'bg-cyan-500 text-white cursor-default'
                            : 'hover:bg-cyan-50 text-gray-900'
                        }`}
                      >
                        {size.title} {isSelected ? '✓' : ''}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Tailles sélectionnées */}
            {selectedSizes.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedSizes.map(size => (
                  <span
                    key={size.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-cyan-100 text-cyan-700 rounded-lg text-sm font-medium"
                  >
                    {size.title}
                    <button
                      type="button"
                      onClick={() => toggleSize(size.id, size.title)}
                      className="hover:text-cyan-900 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Plateformes jeux vidéo - Utilise les options du JSON */}
      {filters?.video_game_platform?.present && filters.video_game_platform.options && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            🎮 {filters.video_game_platform.french_name}
          </label>
          <div className="flex flex-wrap gap-2">
            {filters.video_game_platform.options.map(platform => (
              <button
                key={platform.id}
                type="button"
                onClick={() => togglePlatform(platform.id, platform.title)}
                className={`px-4 py-2 rounded-lg border-2 transition-all font-medium ${
                  selectedPlatforms.some(p => p.id === platform.id)
                    ? 'bg-cyan-500 text-white border-cyan-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-cyan-500'
                }`}
              >
                {platform.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Classification PEGI - Utilise les options du JSON */}
      {filters?.video_game_rating?.present && filters.video_game_rating.options && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            🎮 {filters.video_game_rating.french_name}
          </label>
          <div className="flex flex-wrap gap-2">
            {filters.video_game_rating.options.map(rating => (
              <button
                key={rating.id}
                type="button"
                onClick={() => toggleRating(rating.id, rating.title)}
                className={`px-4 py-2 rounded-lg border-2 transition-all font-medium ${
                  selectedRatings.some(r => r.id === rating.id)
                    ? 'bg-cyan-500 text-white border-cyan-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-cyan-500'
                }`}
              >
                {rating.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Couleurs - Utilise le fichier vintedColors.ts existant */}
      {filters?.color?.present && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            🎨 {filters.color.french_name}
          </label>
          <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto p-2 bg-gray-50 rounded-lg">
            {VINTED_COLORS.map((color: VintedColor) => (
              <button
                key={color.id}
                type="button"
                onClick={() => toggleColor(color.title)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all font-medium text-sm ${
                  selectedColors.includes(color.title)
                    ? 'bg-cyan-500 text-white border-cyan-500 shadow-md'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-cyan-500'
                }`}
              >
                {color.hex && (
                  <span
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: `#${color.hex}` }}
                  ></span>
                )}
                {color.title}
              </button>
            ))}
          </div>
          {selectedColors.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {selectedColors.length} couleur{selectedColors.length > 1 ? 's' : ''} sélectionnée{selectedColors.length > 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}

      {/* Matières - Utilise les options du JSON */}
      {filters?.material?.present && filters.material.options && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            🧵 {filters.material.french_name}
          </label>
          <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto p-2 bg-gray-50 rounded-lg">
            {filters.material.options.map(material => (
              <button
                key={material.id}
                type="button"
                onClick={() => toggleMaterial(material.id, material.title)}
                className={`px-3 py-2 rounded-lg border-2 transition-all font-medium text-sm ${
                  selectedMaterials.some(m => m.id === material.id)
                    ? 'bg-cyan-500 text-white border-cyan-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-cyan-500'
                }`}
              >
                {material.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Motifs - Utilise les options du JSON */}
      {filters?.patterns?.present && filters.patterns.options && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            🎨 {filters.patterns.french_name}
          </label>
          <div className="flex flex-wrap gap-2">
            {filters.patterns.options.map(pattern => (
              <button
                key={pattern.id}
                type="button"
                onClick={() => togglePattern(pattern.id, pattern.title)}
                className={`px-4 py-2 rounded-lg border-2 transition-all font-medium ${
                  selectedPatterns.some(p => p.id === pattern.id)
                    ? 'bg-cyan-500 text-white border-cyan-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-cyan-500'
                }`}
              >
                {pattern.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Langues - Utilise les options du JSON */}
      {filters?.language_book?.present && filters.language_book.options && onLanguagesChange && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            🌍 {filters.language_book.french_name}
          </label>
          <div className="flex flex-wrap gap-2">
            {filters.language_book.options.map(lang => (
              <button
                key={lang.id}
                type="button"
                onClick={() => toggleLanguage(lang.id, lang.title)}
                className={`px-4 py-2 rounded-lg border-2 transition-all font-medium text-sm ${
                  selectedLanguages.some(l => l.id === String(lang.id))
                    ? 'bg-cyan-500 text-white border-cyan-500 shadow-md'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-cyan-500'
                }`}
              >
                {lang.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
